import { VisuallyHidden } from '@automata/ui';
import { logger } from '@automata/utils';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Box, Stack } from '@mui/system';

import { FormSectionLabel } from './FormSectionLabel';
import InvalidDependencyWarning from './InvalidDependencyWarning';
import { deleteTaskDependency } from '../../../features/graph/graphSlice';
import { selectTaskDependenciesErrors } from '../../../features/selectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { useAllTasks, useTask } from '../../../hooks/tasks';
import { getIcon } from '../../customIcons/tasks';

export type Props = {
  taskId: string;
};

export const TaskDependenciesTable = ({
  taskId,
}: Props): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const task = useTask(taskId);
  const tasks = useAllTasks();
  const errors = useAppSelector(selectTaskDependenciesErrors);

  const taskDependenciesAndDependents = [
    ...task.dependencies,
    ...task.dependents,
  ];

  if (taskDependenciesAndDependents.length === 0) return null;

  const hasTaskDependencyErrors =
    errors && taskDependenciesAndDependents.some(({ id }) => errors[id]);

  return (
    <>
      <FormSectionLabel>Task dependencies</FormSectionLabel>
      <Table>
        <TableHead>
          <TableRow>
            {hasTaskDependencyErrors && (
              <TableCell padding="checkbox">
                <VisuallyHidden>Invalid dependencies</VisuallyHidden>
              </TableCell>
            )}
            <TableCell>Relation type</TableCell>
            <TableCell>Linked task</TableCell>
            <TableCell>
              <VisuallyHidden>Actions</VisuallyHidden>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...task.dependencies, ...task.dependents].map(
            ({ id, fromTaskId, toTaskId }) => {
              const otherTask =
                toTaskId === task.id ? tasks[fromTaskId] : tasks[toTaskId];

              if (!otherTask) {
                logger.error("Dependency on task that doesn't exist", {
                  fromTaskId,
                });
                return null;
              }

              const isInvalidDependency = Boolean(errors && errors[id]);

              const TaskIcon = getIcon(otherTask.taskType);
              return (
                <TableRow key={id}>
                  {hasTaskDependencyErrors && (
                    <TableCell padding="none" align="center">
                      {isInvalidDependency && (
                        <InvalidDependencyWarning errors={errors[id] ?? []} />
                      )}
                    </TableCell>
                  )}
                  <TableCell sx={{ width: '140px' }}>
                    Happens {toTaskId === task.id ? 'after' : 'before'}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <TaskIcon color="neutral" fontSize="small" />
                      <Box sx={{ flexGrow: 1 }}>{otherTask.nickname}</Box>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        dispatch(deleteTaskDependency({ id }));
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            }
          )}
        </TableBody>
      </Table>
    </>
  );
};
